'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';

import { Icon } from '@/components/Icon/Icon';
import { Button } from '@/components/UI/Button';
import { cn } from '@/utils/ui';

import config from '../../../tailwind.config';

interface HeaderNav {
  brandIcon?: string;
  children: ReactNode;
  variant: 'mobile' | 'desktop';
}

export function HeaderNav({ children, brandIcon, variant }: HeaderNav): React.ReactNode {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const pathname = usePathname();
  const { width } = useWindowSize();

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [isOpen]);

  useEffect(() => {
    const mdWidthSize = Number(config.theme.screens.md.replace('px', ''));

    if (width >= mdWidthSize && isOpen) {
      setIsOpen(false);
    }
  }, [width, isOpen]);

  useEffect(() => {
    if (pathname) {
      setIsOpen(false);
    }
  }, [pathname]);

  const handleMenuChange = (): void => {
    setIsOpen(!isOpen);
  };

  if (variant === 'mobile') {
    return (
      <>
        <Button
          dataAnalytics="header-mobile-menu-open-button"
          onClick={handleMenuChange}
          className={cn(
            'flex w-[30px] flex-col items-center justify-center bg-transparent p-4 hover:!bg-transparent md:hidden',
            isOpen ? 'absolute right-[15px] z-50 order-5 p-0' : '',
          )}
        >
          <span
            className={cn(
              'block h-0.5 w-6 rounded-sm bg-white transition-all duration-500 ease-out',
              isOpen ? 'translate-y-1 rotate-45' : '-translate-y-0.5',
            )}
          />
          <span
            className={cn(
              'my-0.5 block h-0.5 w-6 rounded-sm bg-white transition-all duration-500 ease-out',
              isOpen ? 'opacity-0' : 'opacity-100',
            )}
          />
          <span
            className={cn(
              'block h-0.5 w-6 rounded-sm bg-white transition-all duration-500 ease-out',
              isOpen ? '-translate-y-1 -rotate-45' : 'translate-y-0.5',
            )}
          />
        </Button>
        <Link
          data-analytics="header-nav-root-link"
          className={cn(
            'z-20 order-2 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
            isOpen ? 'visible pl-[33px] sm:pl-[10px]' : 'hidden',
          )}
          href="/"
        >
          <Icon alt="Fox" className="size-[90px]" id={brandIcon ?? 'brand-fox-one'} />
        </Link>
        <div
          className={cn(
            'fixed left-0 top-[-100vh] z-10 order-3 flex size-full bg-base-black from-10% pt-[94px] backdrop-blur-[27.5px] transition-all duration-500 ease-out md:hidden',
            isOpen ? '!top-0 pb-5' : 'hidden',
          )}
        >
          {children}
        </div>
      </>
    );
  }

  // Desktop variant
  return <div className="absolute left-1/2 hidden -translate-x-1/2 md:flex">{children}</div>;
}
